import { Button } from "@/components/ui/button";
import {
  GitBranch,
  Send,
  Eye,
  PenLine,
  CheckCircle2,
  Clock,
  AlertTriangle,
  RefreshCw,
} from "lucide-react";
import { useLocation } from "wouter";
import {
  computeWorkflowStage,
  approvalStageLabel,
  type ApprovalStatusLight,
} from "@/lib/workflowStages";
import type { ContractLifecycleState } from "@/components/LifecycleStateBadge";

interface PrimaryWorkflowActionProps {
  contractId: number;
  lifecycleState: ContractLifecycleState;
  approval: ApprovalStatusLight | null;
  isAdmin: boolean;
  onRouteForApproval: () => void;
  onSendExternal: () => void;
  /**
   * Detail page URL prefix used when the CTA jumps to a tab on the parent
   * page. Defaults to `/contracts`; the term-sheet detail page passes
   * `/term-sheets` so internal navigation stays inside the TS surface.
   */
  basePath?: string;
  /** When set, workflow stage mapping uses the term-sheet spine. */
  documentKind?: string | null;
  termSheetStatus?: string | null;
}

/**
 * Single, context-aware CTA in the contract detail header that always tells
 * the user the next thing to do (or to wait on). Replaces the previous
 * cluster of "Route for Approval" / "Send Externally" / etc. buttons that
 * forced the user to figure out which one applied.
 *
 * The button label and behavior is derived from the same workflow stage
 * mapping used by the status bar, so the two stay in sync.
 */
export function PrimaryWorkflowAction({
  contractId,
  lifecycleState,
  approval,
  isAdmin,
  onRouteForApproval,
  onSendExternal,
  basePath = "/contracts",
  documentKind = null,
  termSheetStatus = null,
}: PrimaryWorkflowActionProps) {
  const [, setLocation] = useLocation();
  const isTermSheet = documentKind === "term_sheet";
  const stage = computeWorkflowStage(lifecycleState, approval, { documentKind, termSheetStatus });

  const goToApproval = () => {
    setLocation(`${basePath}/${contractId}#approval`);
    requestAnimationFrame(() => window.dispatchEvent(new HashChangeEvent("hashchange")));
  };
  const goToNegotiation = () => {
    setLocation(`${basePath}/${contractId}#negotiation`);
    requestAnimationFrame(() => window.dispatchEvent(new HashChangeEvent("hashchange")));
  };

  switch (stage) {
    case "draft":
      if (!isAdmin) {
        return (
          <Button variant="outline" disabled data-testid="primary-action-draft-viewer">
            <Clock className="h-4 w-4 mr-2" /> Draft — pending admin
          </Button>
        );
      }
      return (
        <Button
          onClick={onRouteForApproval}
          className="bg-[#0B1834] hover:bg-[#0B1834]/90"
          data-testid="primary-action-submit-for-review"
        >
          <GitBranch className="h-4 w-4 mr-2" />
          Submit for Internal Review
        </Button>
      );

    case "internal_review": {
      const stageName = approvalStageLabel(approval?.currentStage ?? null);
      // Returned/rejected get a re-submit affordance.
      if (approval?.status === "returned" || approval?.status === "rejected") {
        const label = approval.status === "rejected" ? "Re-submit (rejected)" : "Address Feedback & Re-submit";
        return (
          <Button
            onClick={isAdmin ? onRouteForApproval : goToApproval}
            variant="outline"
            className="border-amber-400 text-amber-800 hover:bg-amber-50"
            data-testid="primary-action-resubmit"
          >
            <AlertTriangle className="h-4 w-4 mr-2" /> {label}
          </Button>
        );
      }
      return (
        <Button
          variant="outline"
          onClick={goToApproval}
          data-testid="primary-action-awaiting-review"
        >
          <Clock className="h-4 w-4 mr-2" />
          Awaiting {stageName}
        </Button>
      );
    }

    case "ready_to_send":
      if (!isAdmin) {
        return (
          <Button variant="outline" onClick={goToApproval} data-testid="primary-action-ready-viewer">
            <Eye className="h-4 w-4 mr-2" /> Ready to Send — view status
          </Button>
        );
      }
      return (
        <Button
          onClick={onSendExternal}
          className="bg-[#0B1834] hover:bg-[#0B1834]/90"
          data-testid="primary-action-send-externally"
        >
          <Send className="h-4 w-4 mr-2" />
          {isTermSheet ? "Send for Agreement" : "Send Externally"}
        </Button>
      );

    case "negotiation":
      return (
        <Button
          variant="outline"
          onClick={goToNegotiation}
          data-testid="primary-action-view-negotiation"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          {isTermSheet ? "View External Send" : "View Negotiation"}
        </Button>
      );

    case "signature":
      return (
        <Button
          onClick={goToApproval}
          className="bg-purple-600 hover:bg-purple-700"
          data-testid="primary-action-signature-status"
        >
          <PenLine className="h-4 w-4 mr-2" />
          View Signature Status
        </Button>
      );

    case "executed":
      if (isTermSheet) {
        return (
          <Button
            variant="outline"
            onClick={() => {
              setLocation(`${basePath}/${contractId}`);
              requestAnimationFrame(() =>
                document.getElementById("sticky-promote-bar")?.scrollIntoView({ behavior: "smooth" }),
              );
            }}
            className="border-green-300 text-green-800 hover:bg-green-50"
            data-testid="primary-action-ts-accepted"
          >
            <CheckCircle2 className="h-4 w-4 mr-2" />
            {termSheetStatus === "declined" ? "Declined" : "Accepted — Promote"}
          </Button>
        );
      }
      return (
        <Button
          variant="outline"
          onClick={goToApproval}
          className="border-green-300 text-green-800 hover:bg-green-50"
          data-testid="primary-action-executed"
        >
          <CheckCircle2 className="h-4 w-4 mr-2" />
          Executed — View Signed Copy
        </Button>
      );
  }
}
